/**
 * A catalog sync's stored error, kept to one short line (catalog_sync_config.last_sync_error).
 *
 * Errors stored before #955 hold a whole drizzle "Failed query: insert … params: …" dump, which
 * runs to hundreds of lines and swamped the Dashboard's OCC Sync card. This shows the first line,
 * at most SYNC_ERROR_PREVIEW_CHARS, and either a "Show full error" toggle (the Catalog Sync page)
 * or a link to that page (the Dashboard). A successful run clears the error (the runner sets it
 * to NULL), so this only ever shows the latest failure.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { subText } from '../../ui/bits';

export const SYNC_ERROR_PREVIEW_CHARS = 160;

/** The first non-empty line, cut at SYNC_ERROR_PREVIEW_CHARS with an ellipsis. */
export function syncErrorPreview(error: string): { text: string; truncated: boolean } {
  const lines = error.split(/\r?\n/);
  const first = (lines.find((l) => l.trim() !== '') ?? '').trim();
  const cut = first.length > SYNC_ERROR_PREVIEW_CHARS;
  const text = cut ? `${first.slice(0, SYNC_ERROR_PREVIEW_CHARS).trimEnd()}…` : first;
  return { text, truncated: cut || error.trim() !== first };
}

export function SyncErrorText({
  error,
  linkToCatalogSync = false,
}: {
  error: string;
  /** On the Dashboard: link to the Catalog Sync page instead of expanding in place. */
  linkToCatalogSync?: boolean;
}): React.JSX.Element {
  const [open, setOpen] = React.useState(false);
  const { text, truncated } = syncErrorPreview(error);
  const small: React.CSSProperties = { ...subText, fontSize: '12px', textTransform: 'none' };
  return (
    <div style={{ marginTop: '4px', minWidth: 0 }}>
      <div style={{ ...small, overflowWrap: 'anywhere' }} title={truncated ? undefined : error}>
        Last error: {text}
      </div>
      {truncated && linkToCatalogSync && (
        <Link to="/catalog-sync" style={{ ...small, color: 'var(--color-link)' }}>
          See it on Catalog Sync
        </Link>
      )}
      {truncated && !linkToCatalogSync && (
        <>
          <button
            type="button"
            onClick={(): void => setOpen((o) => !o)}
            style={{
              ...small,
              background: 'none',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
              color: 'var(--color-link)',
            }}
            aria-expanded={open}
          >
            {open ? 'Hide full error' : 'Show full error'}
          </button>
          {open && (
            <pre
              style={{
                ...small,
                maxHeight: '240px',
                overflow: 'auto',
                whiteSpace: 'pre-wrap',
                overflowWrap: 'anywhere',
                margin: '4px 0 0',
              }}
            >
              {error}
            </pre>
          )}
        </>
      )}
    </div>
  );
}
